import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import { useApprovedTagEntries } from '@/hooks/useApprovedTagEntries'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useOrgUserIds } from '@/hooks/useOrgUserIds'
import { useStore } from '@/lib/pocketbase'
import { Card } from '@/ui/Card'
import { CollapsibleSection } from '@/ui/CollapsibleSection'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { NotFound } from '@/ui/NotFound'
import { Page } from '@/ui/Page'
import { Tag } from '@/ui/tags/Tag'
import { ApprovedTimeEntriesTable } from '@/ui/time-entry/ApprovedTimeEntriesTable'

export default function TagDetailPage() {
    const params = useLocalSearchParams<{ orgSlug: string; tagId: string }>()
    const theme = useTheme()
    const { userColorMap } = useOrgUserIds()
    const [tagsCollection] = useStore('tags')
    const [fetchEnabled, setFetchEnabled] = useState(false)

    const { data: tag, isLoading } = useLiveQuery(q =>
        q
            .from({ tags: tagsCollection })
            .where(({ tags }) => eq(tags.id, params.tagId || ''))
            .findOne()
    )

    const { entries, isLoading: isLoadingEntries } = useApprovedTagEntries({
        tagId: tag?.id,
        enabled: fetchEnabled,
    })

    const handleExpandedChange = useCallback((expanded: boolean) => {
        if (expanded) {
            setFetchEnabled(true)
        }
    }, [])

    useHeaderOptions({
        buttons: { type: 'edit', href: `/${params.orgSlug}/settings/tags/${tag?.id}/edit` },
        title: tag?.name,
        enabled: !!tag,
    })

    if (isLoading) {
        return <LoadingIndicator />
    }

    if (!tag) {
        return (
            <NotFound message="Tag not found. It may have been deleted or you don't have permission to view it." />
        )
    }

    const createdDate = new Date(tag.created).toLocaleDateString()

    const headerRightContent = (
        <Text
            variant="bodySmall"
            style={{ color: theme.colors.onSurfaceVariant }}
            numberOfLines={1}
        >
            {createdDate}
        </Text>
    )

    return (
        <Page>
            <Card style={styles.collapsibleCard}>
                <CollapsibleSection
                    title={tag.name}
                    icon="label"
                    isLast
                    rightContent={headerRightContent}
                >
                    <View style={{ gap: 16 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Tag tag={tag} />
                            {tag.inactive && (
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    (Inactive)
                                </Text>
                            )}
                        </View>

                        {tag.description && (
                            <View style={{ gap: 8 }}>
                                <View style={styles.labelRow}>
                                    <Icon
                                        source="note-text"
                                        size={16}
                                        color={theme.colors.onSurfaceVariant}
                                    />
                                    <Text variant="bodyLarge" style={{ fontWeight: '600' }}>
                                        Description
                                    </Text>
                                </View>
                                <Text
                                    variant="bodyLarge"
                                    style={{
                                        paddingLeft: 24,
                                        color: theme.colors.onSurfaceVariant,
                                    }}
                                >
                                    {tag.description}
                                </Text>
                            </View>
                        )}

                        <View style={{ gap: 8 }}>
                            <View style={styles.labelRow}>
                                <Icon
                                    source="calendar"
                                    size={16}
                                    color={theme.colors.onSurfaceVariant}
                                />
                                <Text variant="bodyLarge" style={{ fontWeight: '600' }}>
                                    Created
                                </Text>
                            </View>
                            <Text variant="bodyLarge" style={{ paddingLeft: 24 }}>
                                {createdDate}
                            </Text>
                        </View>
                    </View>
                </CollapsibleSection>
            </Card>

            <Card style={styles.collapsibleCard}>
                <CollapsibleSection
                    title="Hours Worked"
                    icon="timetable"
                    isLast
                    onExpandedChange={handleExpandedChange}
                >
                    <ApprovedTimeEntriesTable
                        entries={entries}
                        isLoading={isLoadingEntries}
                        userColorMap={userColorMap}
                    />
                </CollapsibleSection>
            </Card>
        </Page>
    )
}

const styles = StyleSheet.create({
    collapsibleCard: {
        overflow: 'hidden',
    },
    labelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
})
